import { computed, nextTick, ref, watch, type InjectionKey } from 'vue'
import { useDemandeAFStore } from '@/features/interface/demande/af/useDemandeAFStore'
import { storeToRefs } from 'pinia'
import type { ModuleDTO } from '@/features/api/extranet/dto/Demande/AF'
import { useDialog } from '@/features/interface/dialog'
import { BaseCalculFrais, ModeFrais, type NatureFrais } from '@/features/domaine/types/opco/Demande/shared'
import type { TypeModule } from '@/features/domaine/types/opco/Demande/shared/TypeModule'
import type { FraisDTO } from '@/features/api/extranet/dto/Demande/shared'
import {
  HelpFraisAnnexe,
  HelpFraisPedagogique,
  ModalitePedagogique,
  NatureFraisAvecQuantite,
  NaturesFrais,
  TypeQualification,
} from '@/features/domaine/types/opco/Demande/AF'
import type { AdresseDTO, EtablissementPrestataireDTO } from '@/features/api/extranet/dto/shared'
import type { FraisDataParMode } from '@/features/interface/demande/shared/components/frais/Frais.types'
import { SousDispositifLibelle } from '@/features/domaine/types/opco/Demande/AF'
import type { LovItemDTO } from '@/features/api/extranet/dto/Lov'
import type { IconName } from '@designSystem/fondation/icons/iconsList'
import { isNotNullUndefinedOrEmptyString } from '@/features/shared/tools'

export type ModuleAction = ReturnType<typeof useModuleActions>
export const ModuleActionKey = Symbol('module-actions') as InjectionKey<ModuleAction>

export interface ModuleConfig {
  limite?: number
  label: string
  icon: IconName
}

export const MODULE_CONFIG: Record<TypeModule, ModuleConfig> = {
  Interne: {
    limite: undefined,
    label: 'Interne',
    icon: 'building',
  },
  Externe: {
    limite: undefined,
    label: 'Externe',
    icon: 'bank',
  },
}

export function useModuleActions() {
  const store = useDemandeAFStore()
  const { model, readonly } = storeToRefs(store)
  const { setupCustomUpdater } = store
  const dialog = useDialog()

  const idScrollDownToSaisie = computed(() =>
    moduleEnCours.value?.typeModule === 'Externe' ? 'module-scroll-externe' : 'module-scroll-interne',
  )

  const activeModuleIndex = ref<number | null>(null)
  const afficherModale = ref(false)

  const listeModules = computed<ModuleDTO[] | undefined>(() => {
    return model.value?.formation?.modules ?? undefined
  })

  const nombreModules = computed(() => ({
    Interne: listeModules.value?.filter((module) => module.typeModule === MODULE_CONFIG.Interne.label).length ?? 0,
    Externe: listeModules.value?.filter((module) => module.typeModule === MODULE_CONFIG.Externe.label).length ?? 0,
  }))

  const nbMaxModulesAtteint = computed(() => {
    return nombreModules.value.Interne + nombreModules.value.Externe >= 5
  })

  const creerTypeModuleVide = (typeModule: TypeModule): ModuleDTO => {
    const typeQualification: TypeQualification | null =
      store.model?.sousDispositif === 'CleaSocleCompetence'
        ? 'Rs'
        : store.model?.sousDispositif === 'BilanCompetences' ||
            store.model?.sousDispositif === 'TuteurMAitreApprentissage'
          ? 'AttestationStage'
          : null
    const intitule: string | null =
      store.model?.sousDispositif === 'BilanCompetences' ? SousDispositifLibelle[store.model?.sousDispositif] : null
    const formacode: LovItemDTO<string> | null =
      store.model?.sousDispositif === 'BilanCompetences'
        ? { code: '15081', intitule: 'BILAN PROFESSIONNEL' }
        : store.model?.sousDispositif === 'TuteurMAitreApprentissage'
          ? { code: '44588', intitule: 'TUTORAT ENTREPRISE' }
          : null

    return {
      typeModule,
      //referenceFormationInterneEntreprise: null,
      typeQualification,
      modalitePedagogique: null,
      lieuFormation: null,
      rncp: null,
      formacode: formacode,
      repertoireSpecifique: null,
      niveauCertificationVisee: typeQualification === 'AttestationStage' ? 'SansNiveauSpecifique' : null,
      dateDebut: null,
      dateFin: null,
      duree: null,
      fraisPedagogiques: initialiseListeFrais('Pedagogique'),
      fraisAnnexesSalarie: initialiseListeFrais('Annexe'),
      etablissement: null,
      objectifPrestataire: null,
      avecSubrogation: null,
      typeFormation: null,
      dureeFoad: null,
      typeReferentielFormation: null,
      intitule: intitule,
    } as ModuleDTO
  }

  function initialiseListeFrais(mode: ModeFrais) {
    return NaturesFrais[mode].map((nature) => {
      const baseCalcul: BaseCalculFrais = NatureFraisAvecQuantite.includes(nature) ? 'Quantite' : 'Montant'
      return {
        mode,
        nature,
        baseCalcul,
        montant: null,
        quantite: null,
      } as FraisDTO
    })
  }

  const patchAjouterModule = setupCustomUpdater<TypeModule>((patch, typeModule) => {
    const nouveauModule = creerTypeModuleVide(typeModule)

    if (!patch.formation?.modules) patch.formation!.modules = []
    patch.formation?.modules?.push(nouveauModule)
  })

  async function scrollDownToSaisie() {
    await nextTick()
    await nextTick()
    document.getElementById(idScrollDownToSaisie.value)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  function définirModuleEnCours(index: number | null) {
    const isTogglingOff = index === activeModuleIndex.value
    activeModuleIndex.value = isTogglingOff ? null : index
    if (activeModuleIndex.value !== null) {
      scrollDownToSaisie()
    }
  }

  async function ajouterModule(typeModule: TypeModule) {
    await patchAjouterModule(typeModule)
    activeModuleIndex.value = model.value!.formation!.modules!.length - 1
    afficherModale.value = false
    définirModuleEnCours(activeModuleIndex.value)
  }

  async function supprimerModule(index: number) {
    const module = listeModules.value![index]

    const result = await dialog.showDialog({
      message: `Confirmez-vous la suppression du module de type ${module.typeModule} ?`,
      type: 'YesNo',
      title: 'Supprimer un module',
    })

    if (result === 'Yes') {
      await supprimerModuleDuModel(index)

      if (activeModuleIndex.value === index) {
        activeModuleIndex.value = null
      } else if (activeModuleIndex.value !== null && activeModuleIndex.value > index) {
        activeModuleIndex.value -= 1
      }
    }
  }

  const supprimerModuleDuModel = setupCustomUpdater<number>((patch, index) => {
    const element = patch.formation?.modules?.splice(index, 1)
    if (!element) throw 'Index inexistant'
  })

  const moduleEnCours = computed<ModuleDTO | undefined>(() => {
    if (activeModuleIndex.value === null) return
    return listeModules.value![activeModuleIndex.value]
  })

  const metaModuleEnCours = computed(() => {
    if (typeof activeModuleIndex.value !== 'number') return null
    return store.meta.formation?.modules?.at(activeModuleIndex.value)
  })

  const fraisDataParMode = computed<FraisDataParMode>(() => {
    return {
      Pedagogique: {
        frais: moduleEnCours.value?.fraisPedagogiques || [],
        metaFrais: metaModuleEnCours.value?.fraisPedagogiques,
        fraisTitre: 'Frais Pédagogique',
        fraisField: 'fraisPedagogiques',
        fraisHelp: HelpFraisPedagogique,
      },
      Annexe: {
        frais: moduleEnCours.value?.fraisAnnexesSalarie || [],
        metaFrais: metaModuleEnCours.value?.fraisAnnexesSalarie,
        fraisTitre: 'Frais Annexe',
        fraisField: 'fraisAnnexesSalarie',
        fraisHelp: HelpFraisAnnexe,
      },
    }
  })

  const modifieFrais = setupCustomUpdater<{
    mode: ModeFrais
    nature: NatureFrais
    field: keyof FraisDTO
    valeur?: string | number | null
  }>((patch, { mode, nature, field, valeur }) => {
    const modulePatch = patch.formation!.modules![activeModuleIndex.value!]!

    const fraisField = fraisDataParMode.value[mode]?.fraisField as keyof ModuleDTO

    const listeFrais = modulePatch[fraisField] as FraisDTO[]

    const fraiAModifier = listeFrais.find((f) => f.nature === nature)
    if (!fraiAModifier) {
      throw new Error(`Aucun frais trouvé pour la nature ${nature}`)
    }

    fraiAModifier[field] = valeur as any
  })

  function dupliqueAdresse(adresse: AdresseDTO): AdresseDTO {
    return {
      codePostal: adresse.codePostal ?? null,
      commune: adresse.commune ?? null,
      complement: adresse.complement ?? null,
      voie: adresse.voie ?? null,
      mentionsSpeciales: adresse.mentionsSpeciales ?? null,
    }
  }

  const updateModalitePedagogique = setupCustomUpdater<ModalitePedagogique | null | undefined>((patch, modalite) => {
    patch.formation!.modules!.at(activeModuleIndex.value!)!.modalitePedagogique = modalite
    if (modalite === 'Afest' || modalite === 'Distance') {
      patch.formation!.modules!.at(activeModuleIndex.value!)!.lieuFormationIdentiqueOf = null
    }
  })

  const updateMemeLieu = setupCustomUpdater<boolean | null | undefined>((patch, memeLieu) => {
    patch.formation!.modules!.at(activeModuleIndex.value!)!.lieuFormationIdentiqueOf = memeLieu

    if (memeLieu === true) {
      const adresse = dupliqueAdresse(
        (moduleEnCours.value?.typeModule === 'Externe'
          ? moduleEnCours.value!.etablissement?.adresse
          : model.value?.employeur?.etablissement?.adresse) ?? ({} as AdresseDTO),
      )
      const truncatedAdresse = {
        ...adresse,
        complement: adresse.complement?.substring(0, 37) || null
      }
      patch.formation!.modules!.at(activeModuleIndex.value!)!.lieuFormation = truncatedAdresse
    } else {
      patch.formation!.modules!.at(activeModuleIndex.value!)!.lieuFormation = null
    }
  })

  const updateEtablissement = store.setupCustomUpdater<EtablissementPrestataireDTO | null>((patch, etablissement) => {
    const conserverAdresse = moduleEnCours.value?.lieuFormationIdentiqueOf
    patch.formation!.modules!.at(activeModuleIndex.value!)!.etablissement = etablissement
    if (conserverAdresse) {
      const estSiretValide = isNotNullUndefinedOrEmptyString(etablissement?.siret?.valeur)
      if (estSiretValide) {
        patch.formation!.modules!.at(activeModuleIndex.value!)!.lieuFormation = dupliqueAdresse(
          etablissement!.adresse ?? ({} as AdresseDTO),
        )
      } else {
        patch.formation!.modules!.at(activeModuleIndex.value!)!.lieuFormation = null
      }
    }
  })

  const selectedModuleIndex = ref<number | null>(null)

  const setActiveCard = (index: number) => {
    selectedModuleIndex.value = selectedModuleIndex.value === index ? null : index
  }

  const cards = computed(() => [
    {
      title: `Formation ${MODULE_CONFIG.Interne.label}`,
      iconName: MODULE_CONFIG.Interne.icon,
      disabled: nombreModules.value.Interne >= MODULE_CONFIG.Interne.limite!,
      typeModule: 'Interne' as TypeModule,
    },
    {
      title: `Formation ${MODULE_CONFIG.Externe.label}`,
      iconName: MODULE_CONFIG.Externe.icon,
      disabled: nombreModules.value.Externe >= MODULE_CONFIG.Externe.limite!,
      typeModule: 'Externe' as TypeModule,
    },
  ])

  watch(
    () => afficherModale.value,
    (newVal) => newVal && (selectedModuleIndex.value = null),
  )

  return {
    // États et actions liés aux modules
    afficherModale,
    activeModuleIndex,
    nombreModules,
    nbMaxModulesAtteint,
    ajouterModule,
    définirModuleEnCours,
    supprimerModule,
    idScrollDownToSaisie,
    setActiveCard,
    cards,
    selectedModuleIndex,

    listeModules,
    moduleEnCours,
    metaModuleEnCours,
    // Actions liées aux frais
    modifieFrais,
    readonly,
    fraisDataParMode,
    // information Modules
    updateEtablissement,
    updateModalitePedagogique,
    updateMemeLieu,
  }
}
